// pl-charts.ts — Power-Level breakdown charts (bar + pie + toggle + info panel).
// Byte-faithful port of the PL chart cluster carved out of template.js (T1.1 inventory-breakdown
// carve, 2026-06-13). The Chart.js destroy-before-recreate lifecycle is preserved verbatim
// (canvas-leak avoidance). Public (window-attached): switchPlChart (the breakdown ledger's chart toggle) +
// openPowerLevelInfo (PHP onclick: _creature_header_inc.php / table_item_object_inc.php + the
// object-card bundle onclick strings). initializePlBarChart / initializePlPieChart stay
// module-local — they have no external callers and are reached only via switchPlChart /
// openPowerLevelInfo. Loaded AFTER template.js so queryData / forceOpenSideInfoPanel /
// sideInfoActivateItemTab / BASE_URL resolve at call time; Chart / ChartDataLabels are vendor globals.

import { projectPlReviewDefinitions } from './pl-review-definitions';

const w = window;

function chartStringList(raw: string | undefined): string[] {
    try {
        const value: unknown = JSON.parse(raw || '[]');
        return Array.isArray(value) ? value.map(String) : [];
    } catch { return []; }
}

function chartNumberList(raw: string | undefined): number[] {
    try {
        const value: unknown = JSON.parse(raw || '[]');
        return Array.isArray(value) ? value.map(Number) : [];
    } catch { return []; }
}

// PL BAR CHART — v2.8 (sorted descending + safe data reading)
async function initializePlBarChart() {
    const canvas = document.getElementById('plBarChart') as InventoryBreakdownCanvas | null;
    if (!canvas) return;

    if (canvas._plBarChart) {
        canvas._plBarChart.destroy();
        canvas._plBarChart = null;
    }

    // Get data from pie canvas (still the source of truth)
    const pieCanvas = document.getElementById('plPieChart') as InventoryBreakdownCanvas | null;
    if (!pieCanvas) return;

    const labels = chartStringList(pieCanvas.dataset.labels);
    const values = chartNumberList(pieCanvas.dataset.values);

    // Sort descending
    const sorted = labels.map((label, i) => ({ label, value: values[i] ?? 0 }))
                        .sort((a, b) => b.value - a.value);

    canvas._plBarChart = new Chart(canvas, {
        type: 'bar',
        data: {
            labels: sorted.map((item) => item.label),
            datasets: [{
                data: sorted.map((item) => item.value),
                backgroundColor: '#0d6efd',
                borderColor: '#212529',
                borderWidth: 2
            }]
        },
        options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx: { raw: unknown }) => `${String(ctx.raw)} PL`
                    }
                }
            },
            scales: {
                x: { beginAtZero: true, grid: { color: '#343a40' } },
                y: { grid: { color: '#343a40' } }
            }
        }
    });
}

// PL PIE CHART — v2.46 (Number + % inside slices + DROP SHADOW)
async function initializePlPieChart() {
    const canvas = document.getElementById('plPieChart') as InventoryBreakdownCanvas | null;
    if (!canvas) return;

    if (canvas._plPieChart) {
        canvas._plPieChart.destroy();
        canvas._plPieChart = null;
    }

    const labels = chartStringList(canvas.dataset.labels);
    const values = chartNumberList(canvas.dataset.values);
    const colors = chartStringList(canvas.dataset.colors);

    if (labels.length === 0) return;

    const total = values.reduce((sum, v) => sum + v, 0);

    const ctx = canvas.getContext('2d');

    canvas._plPieChart = new Chart(ctx, {
        type: 'pie',
        data: {
            labels: labels,
            datasets: [{
                data: values,
                backgroundColor: colors,
                borderColor: '#212529',
                borderWidth: 3
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: 15 },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: function(ctx: { raw: number }) {
                            const perc = ((ctx.raw / total) * 100).toFixed(0);
                            return `${ctx.raw} PL (${perc}%)`;
                        }
                    }
                },
                datalabels: {
                    color: '#ffffff',
                    font: { size: 11, weight: '700' },
                    formatter: (value: number) => {
                        const perc = ((value / total) * 100).toFixed(0);
                        return `${value}\n${perc}%`;
                    },
                    anchor: 'center',
                    align: 'center',
                    // === DROP SHADOW ===
                    shadowBlur: 4,
                    shadowColor: 'rgba(0, 0, 0, 0.85)',
                    shadowOffsetX: 1,
                    shadowOffsetY: 1
                }
            }
        },
        plugins: [ChartDataLabels]
    });
}

// CHART TOGGLE — v2.8 (now safely re-inits pie on toggle)
function switchPlChart(type: unknown) {
    const pieCanvas = document.getElementById('plPieChart') as InventoryBreakdownCanvas | null;
    const barCanvas = document.getElementById('plBarChart') as InventoryBreakdownCanvas | null;
    const btnPie = document.getElementById('btn-pie');
    const btnBar = document.getElementById('btn-bar');
    if (!pieCanvas || !barCanvas || !btnPie || !btnBar) return;

    if (type === 'pie') {
        pieCanvas.style.display = 'block';
        barCanvas.style.display = 'none';
        btnPie.classList.add('active');
        btnBar.classList.remove('active');
        initializePlPieChart();        // ← re-init when switching back
    } else {
        pieCanvas.style.display = 'none';
        barCanvas.style.display = 'block';
        btnPie.classList.remove('active');
        btnBar.classList.add('active');
        initializePlBarChart();
    }
}

// C2c (2026-09-17): the breakdown is the BROWSER's own pricing — window.ftPlBreakdown (the Janus PL module, shipped in the same
// post-template aggregate) prices the row status-free and renders the ledger; the PHP tool options/tools/items/pl_breakdown/ is retired.
type PlBreakdownAnswer = { html?: string; refusal?: string; stored?: number | null; name?: string };
type PlBreakdownFn = (id: string, opts?: { fullPageLink?: boolean }) => Promise<PlBreakdownAnswer>;
function plBreakdownFn(): PlBreakdownFn | null {
    const fn = (window as unknown as { ftPlBreakdown?: PlBreakdownFn }).ftPlBreakdown;
    return typeof fn === 'function' ? fn : null;
}
function plEsc(v: unknown): string {
    return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
/** Render the breakdown for an id into a container. Returns true when a ledger was drawn. */
async function renderPlBreakdownInto(el: HTMLElement, id: string, fullPageLink: boolean): Promise<boolean> {
    el.innerHTML = '<div class="text-center py-4 opacity-75"><div class="spinner-border spinner-border-sm me-2" role="status"></div>Pricing in your browser…</div>';
    const fn = plBreakdownFn();
    if (!fn) { el.innerHTML = '<div class="alert alert-warning small">The PL engine is not loaded on this page.</div>'; return false; }
    const answer = await fn(id, { fullPageLink });
    if (answer.html) {
        el.innerHTML = answer.html;
        const mount = (window as unknown as { ftMountIcons?: (root?: Element) => void }).ftMountIcons;
        if (typeof mount === 'function') mount(el);
        return true;
    }
    const stored = typeof answer.stored === 'number' ? `<div class="mt-2">Stored value: <strong>${answer.stored.toFixed(2)} PL</strong></div>` : '';
    el.innerHTML = `<div class="alert alert-secondary small text-start"><strong>${plEsc(answer.name || 'This item')}</strong> could not be priced in the browser.<div class="opacity-75 mt-1"><code>${plEsc(answer.refusal ?? 'unpriced')}</code></div>${stored}</div>`;
    return false;
}

async function openPowerLevelInfo(item_id: unknown){
    forceOpenSideInfoPanel();
    sideInfoActivateItemTab();
    const el = document.getElementById('div_refresh_side_info');
    if (!el) return;
    if (await renderPlBreakdownInto(el, String(item_id ?? ''), true)) await initializePlPieChart();
}

// The full-page view (options/pl_breakdown_full.php) mounts here.
function mountPlBreakdownFullPage(): void {
    const el = document.getElementById('ft-pl-breakdown-full');
    const id = el?.dataset.itemId ?? '';
    if (!el || !id || el.dataset.mounted === '1') return;
    el.dataset.mounted = '1';
    void renderPlBreakdownInto(el, id, false).then(ok => { if (ok) void initializePlPieChart(); });
}
if (typeof document !== 'undefined') {
    if (document.readyState === 'complete') mountPlBreakdownFullPage();
    else window.addEventListener('load', mountPlBreakdownFullPage);
}

// --- public surface (bare-identifier globals reached by PHP onclick + cross-bundle callers) ---
Object.assign(w, { switchPlChart, openPowerLevelInfo, ftPlReviewDefinitions: projectPlReviewDefinitions });

export {};
